import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, map, switchMap, tap } from 'rxjs';
import { SILENT_ERROR } from '../error.interceptor';

export interface AuthUser {
  username: string;
  email?: string;
}

interface BaseOutput<T> {
  data: T;
}

const TOKEN_STORAGE_KEY = 'backoffice.jwt';

function decodeJwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Password + OTP authentication against auth-service, reached through api-gateway
 * (see proxy.conf.json / nginx.conf: "/auth" -> api-gateway -> auth-service).
 *
 * Two-step login: POST /auth/v1/authenticate/login (username/password) returns a
 * short-lived preAuthToken; POST /auth/v1/authenticate/verify-otp (preAuthToken + emailed
 * code) returns the session JWT. The JWT itself only carries username/sessionId, so the
 * richer profile (email, ...) is fetched separately via authenticateByToken.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  readonly isAuthenticated = signal(false);
  readonly currentUser = signal<AuthUser | null>(null);

  private token: string | null = null;

  /** Called once during APP_INITIALIZER, before any authenticated API call. */
  async init(): Promise<boolean> {
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) {
      return false;
    }

    // Undecodable payload (expiresAt === null) is treated as invalid, not as "no expiry" —
    // a malformed/stale token must not leave the user stuck in an authenticated-but-broken state.
    const expiresAt = decodeJwtExpiry(stored);
    if (expiresAt === null || expiresAt <= Date.now()) {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      return false;
    }

    this.token = stored;
    this.isAuthenticated.set(true);
    // Best-effort: an expired/invalid session on the backend surfaces as a 401 on the
    // next authenticated call, handled by authGuard/errorInterceptor as usual.
    await this.refreshCurrentUser().catch(() => undefined);
    return this.isAuthenticated();
  }

  /** Step 1: checks credentials, triggers the OTP email. Returns the preAuthToken. */
  login(username: string, password: string): Observable<string> {
    return this.http
      .post<BaseOutput<string>>('/auth/v1/authenticate/login', { username, password })
      .pipe(map(res => res.data));
  }

  /** Step 2: redeems the OTP, opens the session, and populates the current user profile. */
  verifyOtp(preAuthToken: string, code: string): Observable<void> {
    return this.http
      .post<BaseOutput<string>>('/auth/v1/authenticate/verify-otp', { preAuthToken, code })
      .pipe(
        map(res => res.data),
        tap(jwt => {
          this.token = jwt;
          sessionStorage.setItem(TOKEN_STORAGE_KEY, jwt);
          this.isAuthenticated.set(true);
        }),
        switchMap(() => from(this.refreshCurrentUser())),
      );
  }

  /** Returns the stored JWT, if any. No automatic refresh (session lasts the JWT's TTL). */
  getValidToken(): string | null {
    return this.token;
  }

  logout(): void {
    this.token = null;
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.router.navigateByUrl('/login');
  }

  private async refreshCurrentUser(): Promise<void> {
    const user = await this.http
      .get<BaseOutput<AuthUser>>('/auth/v1/authenticate/authenticateByToken', {
        context: new HttpContext().set(SILENT_ERROR, true),
      })
      .pipe(map(res => res.data))
      .toPromise();
    this.currentUser.set(user ?? null);
  }
}
